import React, { useState, useEffect } from 'react';
import { X, Save, FileText, History, AlertCircle, Database } from 'lucide-react';
import { Publication, DataSource } from '../../types';
import { publicationAPI, dataSourcesAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface EditPublicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  publication: Publication | null;
  onUpdate: () => void;
}

interface EditHistory {
  id: number;
  field: string;
  old_value: string;
  new_value: string;
  edited_by_name: string;
  edited_at: string;
}

const EditPublicationModal: React.FC<EditPublicationModalProps> = ({ 
  isOpen, 
  onClose, 
  publication, 
  onUpdate 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    abstract: '',
    methods: '',
    dataSources: [] as number[]
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [editHistory, setEditHistory] = useState<EditHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [isLoadingDataSources, setIsLoadingDataSources] = useState(false);

  useEffect(() => {
    if (publication && isOpen) {
      setFormData({
        title: publication.title || '',
        abstract: publication.abstract || '',
        methods: publication.methods || '',
        dataSources: publication.dataSources ? publication.dataSources.map(ds => ds.id) : []
      });
      loadEditHistory();
      loadDataSources();
    }
  }, [publication, isOpen]);

  const loadDataSources = async () => {
    try {
      setIsLoadingDataSources(true);
      const sources = await dataSourcesAPI.getDataSources();
      setDataSources(sources);
    } catch (error) {
      console.error('Failed to load data sources:', error);
      toast.error('Failed to load data sources');
    } finally {
      setIsLoadingDataSources(false);
    }
  };

  const loadEditHistory = async () => {
    if (!publication) return;
    
    try {
      const history = await publicationAPI.getEditHistory(publication.id);
      setEditHistory(history);
    } catch (error) {
      console.error('Failed to load edit history:', error);
      setEditHistory([]);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.trim().length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    }

    if (!formData.abstract.trim()) {
      newErrors.abstract = 'Abstract is required';
    } else if (formData.abstract.trim().length < 50) {
      newErrors.abstract = 'Abstract must be at least 50 characters';
    }

    if (!formData.methods.trim()) {
      newErrors.methods = 'Proposed Methods is required';
    } else if (formData.methods.trim().length < 10) {
      newErrors.methods = 'Proposed Methods must be at least 10 characters';
    }

    if (!formData.dataSources || formData.dataSources.length === 0) {
      newErrors.dataSources = 'Please select at least one data source';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!publication || !validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // Check if there are actual changes
      const currentDataSources = publication.dataSources ? publication.dataSources.map(ds => ds.id) : [];
      const hasChanges = 
        formData.title !== publication.title || 
        formData.abstract !== publication.abstract ||
        formData.methods !== (publication.methods || '') ||
        JSON.stringify(formData.dataSources.sort()) !== JSON.stringify(currentDataSources.sort());

      if (!hasChanges) {
        toast('No changes detected', { icon: 'ℹ️' });
        onClose();
        return;
      }

      await publicationAPI.updatePublication(publication.id, {
        title: formData.title,
        abstract: formData.abstract,
        methods: formData.methods,
        dataSources: formData.dataSources
      });

      // Clear cache to ensure fresh data on next load
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern(`/publications/${publication.id}/batch`);
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');

      toast.success('Publication updated successfully!');
      onUpdate();
      loadEditHistory(); // Refresh the history
      onClose();
    } catch (error: any) {
      const apiErrors = error?.response?.data?.errors as Array<{ msg: string }> | undefined;
      if (apiErrors && apiErrors.length) {
        const errorMap: Record<string, string> = {};
        apiErrors.forEach(err => {
          if (err.msg.includes('title')) errorMap.title = err.msg;
          else if (err.msg.includes('abstract')) errorMap.abstract = err.msg;
        });
        setErrors(errorMap);
      } else {
        toast.error(error?.response?.data?.error || 'Failed to update publication');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleDataSourceChange = (dataSourceId: number, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      dataSources: checked 
        ? [...prev.dataSources, dataSourceId]
        : prev.dataSources.filter(id => id !== dataSourceId)
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen || !publication) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-green-600" />
            Edit Publication
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center space-x-1 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <History className="h-4 w-4" />
              <span className="text-sm">History</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {showHistory && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <History className="h-5 w-5 mr-2 text-green-600" />
                Edit History
              </h3>
              {editHistory.length > 0 ? (
                <div className="space-y-3">
                  {editHistory.map((entry) => (
                    <div key={entry.id} className="p-3 bg-white rounded-lg border">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium text-gray-900 capitalize">
                          {entry.field} edited
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatDate(entry.edited_at)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mb-1">
                        <strong>Edited by:</strong> {entry.edited_by_name}
                      </div>
                      <div className="text-sm">
                        <div className="text-red-600 line-through">
                          <strong>Old:</strong> {entry.old_value}
                        </div>
                        <div className="text-green-600">
                          <strong>New:</strong> {entry.new_value}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">No edit history available.</p>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  errors.title ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter publication title"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.title}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="abstract" className="block text-sm font-medium text-gray-700 mb-2">
                Abstract *
              </label>
              <textarea
                id="abstract"
                value={formData.abstract}
                onChange={(e) => handleInputChange('abstract', e.target.value)}
                rows={8}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  errors.abstract ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter publication abstract"
              />
              {errors.abstract && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.abstract}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="methods" className="block text-sm font-medium text-gray-700 mb-2">
                Proposed Methods *
              </label>
              <textarea
                id="methods"
                value={formData.methods}
                onChange={(e) => handleInputChange('methods', e.target.value)}
                rows={6}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  errors.methods ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter proposed methods"
              />
              {errors.methods && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.methods}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Database className="inline h-4 w-4 mr-1" />
                Data Sources * (Select all that apply)
              </label>
              {isLoadingDataSources ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-600">Loading data sources...</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-3">
                  {dataSources.map((source) => (
                    <label key={source.id} className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={formData.dataSources.includes(source.id)}
                        onChange={(e) => handleDataSourceChange(source.id, e.target.checked)}
                        className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{source.name}</div>
                        {source.description && (
                          <div className="text-xs text-gray-500 mt-1">{source.description}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              {errors.dataSources && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.dataSources}
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save className="h-4 w-4" />
                <span>{isLoading ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditPublicationModal;
